import React, { useState, useCallback } from "react";
import {
	DndContext,
	closestCenter,
	PointerSensor,
	KeyboardSensor,
	useSensor,
	useSensors,
	DragEndEvent,
	DragOverlay,
	DragStartEvent,
} from "@dnd-kit/core";
import { Check, Loader2 } from "lucide-react";
import { useServiceCatalogStore } from "../../store/ServiceCatalog";
import { useAutoSave } from "../../hooks/useAutosave/useAutosave";
import { ServiceCatalogItem } from "../../store/ServiceCatalog/types";
import { ServiceCatalogTreePanel } from "./components/service-catalog-tree-panel.component";
import { ServiceCatalogBrowserPanel } from "./components/service-catalog-browser-panel.component";

interface DragActiveData {
	type: "folder" | "item" | "item-tail" | "browser-item";
	badge?: string;
	code?: string;
	description?: string;
	catalogItem?: ServiceCatalogItem;
	parentId?: string | null;
	index?: number;
}

export const ServiceCatalogPage: React.FC = () => {
	const catalog = useServiceCatalogStore((s) => s.catalog);
	const hasUnsavedChanges = useServiceCatalogStore(
		(s) => s.hasUnsavedChanges
	);
	const saveCatalog = useServiceCatalogStore((s) => s.saveCatalog);
	const reorderFolders = useServiceCatalogStore((s) => s.reorderFolders);
	const reorderItems = useServiceCatalogStore((s) => s.reorderItems);
	const moveItem = useServiceCatalogStore((s) => s.moveItem);
	const addItem = useServiceCatalogStore((s) => s.addItem);

	const [dragActiveData, setDragActiveData] = useState<DragActiveData | null>(
		null
	);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 8 },
		}),
		useSensor(KeyboardSensor)
	);

	useAutoSave({
		data: catalog,
		onSave: saveCatalog,
		shouldSave: hasUnsavedChanges,
		delay: 500,
	});

	const handleDragStart = useCallback((event: DragStartEvent) => {
		const data = event.active.data.current as DragActiveData;
		setDragActiveData(data ?? null);
	}, []);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			setDragActiveData(null);
			const { active, over } = event;
			if (!over || active.id === over.id) return;

			const activeData = active.data.current as DragActiveData;
			const overData = over.data.current as DragActiveData;
			if (!activeData || !overData) return;

			if (activeData.type === "browser-item") {
				if (!activeData.catalogItem) return;
				if (
					(overData.type === "item" ||
						overData.type === "item-tail") &&
					overData.parentId
				) {
					addItem(
						overData.parentId,
						activeData.catalogItem,
						overData.index
					);
				} else if (overData.type === "folder") {
					addItem(String(over.id), activeData.catalogItem);
				}
				return;
			}

			if (
				activeData.type === "folder" &&
				overData.type === "folder" &&
				activeData.parentId === overData.parentId
			) {
				reorderFolders(
					activeData.parentId!,
					activeData.index!,
					overData.index!
				);
			} else if (activeData.type === "item" && overData.type === "item") {
				if (activeData.parentId === overData.parentId) {
					reorderItems(
						activeData.parentId!,
						activeData.index!,
						overData.index!
					);
				} else {
					moveItem(
						activeData.parentId!,
						activeData.index!,
						overData.parentId!,
						overData.index!
					);
				}
			} else if (
				activeData.type === "item" &&
				overData.type === "item-tail" &&
				overData.parentId
			) {
				if (activeData.parentId === overData.parentId) {
					reorderItems(
						activeData.parentId!,
						activeData.index!,
						overData.index! - 1
					);
				} else {
					moveItem(
						activeData.parentId!,
						activeData.index!,
						overData.parentId,
						overData.index!
					);
				}
			}
		},
		[reorderFolders, reorderItems, moveItem, addItem]
	);

	return (
		<div className="flex flex-col h-full">
			<div className="flex items-center justify-between px-6 py-3 border-b">
				<h1 className="text-lg font-semibold">Leistungskatalog</h1>
				<span
					className="flex items-center gap-1.5 text-xs text-muted-foreground"
					data-testid="service-catalog-save-status"
				>
					{hasUnsavedChanges ? (
						<>
							<Loader2 className="h-3 w-3 animate-spin" />
							Änderungen ausstehend
						</>
					) : (
						<>
							<Check className="h-3 w-3" />
							Gespeichert
						</>
					)}
				</span>
			</div>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
			>
				<div className="flex flex-1 overflow-hidden">
					<div className="w-[60%] border-r overflow-auto">
						<ServiceCatalogTreePanel />
					</div>
					<div className="w-[40%] overflow-auto">
						<ServiceCatalogBrowserPanel />
					</div>
				</div>
				<DragOverlay>
					{dragActiveData ? (
						<div className="bg-white border rounded px-3 py-1 shadow-md text-sm opacity-80">
							{dragActiveData.type === "browser-item" ? (
								<span className="flex items-center gap-2">
									<span className="font-mono font-semibold">
										{dragActiveData.badge}
									</span>
									<span className="font-mono text-muted-foreground">
										{dragActiveData.code}
									</span>
									<span className="truncate max-w-[200px]">
										{dragActiveData.description}
									</span>
								</span>
							) : (
								"Verschieben..."
							)}
						</div>
					) : null}
				</DragOverlay>
			</DndContext>
		</div>
	);
};
